import { SHButton, SHDialog, SHStack } from "@components/design-systems";
import { useFeeCalculatorPortfolioSetup } from "@hooks/useFeeCalculatorPortfolioSetup";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { useUserPermissions } from "@hooks/userUserPermission";
import {
  AnalysisFeePortfolioDTO,
  AnalysisFeePortfolioSetupDTO,
  FeeCalculatorRenderFieldProps,
  PortfolioAssignmentItemDTO,
} from "@models/platform-analysis/entities/steps/fee";
import { useTheme } from "@mui/material";
import { debounce, isEqual, sortBy } from "lodash";
import { memo, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useToggle, useUnmount } from "react-use";
import { AnalysisFeePortfolioSetupModal } from "./modals";
import { SetupItem } from "./setup-item";
import {
  compileOverwritePortfolioValidationResult,
  getPortfolioAssignmentItem,
} from "./utils";

export const FeeCalculatorPortfolioSetup = memo(
  ({ disabled }: FeeCalculatorRenderFieldProps) => {
    const { analysisId } = useParams<{ analysisId: string }>();
    const { isSupplierGroup } = useUserPermissions();
    const { palette } = useTheme();
    const {
      checkAnalysisPortfolioAssignment,
      setFeeCalculator,
      setInitialValidatedPortfolioSetup,
    } = usePlatformAnalysis();

    const {
      calculatedTotals,
      portfolioSetups,
      portfolios,
      portfolioSelections,
      feeCalculators,
      useFeeCalculatorReturnValues: { hasIDPS, hasSuper },
      investmentTypeSelections,
      getFormValues,
      updatePortfolios,
      updatePortfolioSetups,
    } = useFeeCalculatorPortfolioSetup();

    // #region States
    const [modalOpen, toggleModalOpen] = useToggle(false);
    const [modalNoBalance, toggleNoBalanceModal] = useToggle(false);
    const [modalType, setModalType] = useState<"create" | "edit" | undefined>();
    const [isValidatePortfolioSetup, toggleIsValidatePortfolioSetup] =
      useToggle(false);

    const [selectedPortfolioSetup, setSelectedPortfolioSetup] = useState<
      AnalysisFeePortfolioSetupDTO | undefined
    >();
    const [selectedPortfolioSetupIndex, setSelectedPortfolioSetupIndex] =
      useState<number | undefined>();
    const [portfolio, setPortfolio] = useState<
      AnalysisFeePortfolioDTO | undefined
    >(undefined);
    // #endregion States

    // #region Handlers
    const onEditPortfolio = ({
      portfolioId,
      setupIndex,
    }: {
      portfolioId: string;
      setupIndex: number;
    }) => {
      if (!hasIDPS && !hasSuper) {
        toggleNoBalanceModal(true);
        return;
      }

      const setup = portfolioSetups?.[setupIndex];
      setSelectedPortfolioSetup(setup);
      setSelectedPortfolioSetupIndex(setupIndex);
      const portfolio = portfolios?.find((x) => x.id === portfolioId);
      setPortfolio(portfolio);
      setModalType("edit");
      toggleModalOpen(true);
    };

    const onCreatePortfolio = (setupIndex: number) => {
      if (!hasIDPS && !hasSuper) {
        toggleNoBalanceModal(true);
        return;
      }

      const setup = portfolioSetups?.[setupIndex];
      setSelectedPortfolioSetup(setup);
      setSelectedPortfolioSetupIndex(setupIndex);
      setModalType("create");
      toggleModalOpen(true);
    };

    const handleOnNewPortfolioCreated = (
      createdPortfolio: AnalysisFeePortfolioDTO,
      setupIndex: number,
    ) => {
      // Update portfolio list
      let currentPortfolios = structuredClone(portfolios) || [];
      currentPortfolios.push(createdPortfolio);
      currentPortfolios = sortBy(currentPortfolios, (p) => p.order);
      updatePortfolios(currentPortfolios);

      // Assign the newly created portfolio
      let currentPortfolioSetups: AnalysisFeePortfolioSetupDTO[] =
        structuredClone(portfolioSetups) || [];

      currentPortfolioSetups = currentPortfolioSetups?.map(
        (item, itemIndex) => {
          if (itemIndex !== setupIndex) return item;

          const setup: AnalysisFeePortfolioSetupDTO = {
            ...item,
            hasAllocationError: false,
            isMissingInvestment: false,
            missingInvestmentTooltip: null,
            isMissingInvestmentOption: false,
            missingInvestmentOptionTooltip: null,
            hasUnavailableInvestmentOption: false,
            unavailableInvestmentOptionTooltip: null,
            hasUnavailableInvestment: false,
            unavailableInvestmentTooltip: null,
            hasCashNotAvailableInSuperError: false,
            hasCashNotAvailableInIDPSError: false,
            isBelowMinimumInvestmentSize:
              !!createdPortfolio.belowMinimumInvestmentSizeTooltip,
            belowMinimumInvestmentSizeTooltip:
              createdPortfolio.belowMinimumInvestmentSizeTooltip,
            selectedPortfolioId: createdPortfolio.id,
          };

          return setup;
        },
      );
      updatePortfolioSetups(currentPortfolioSetups);

      // Update the fee calculator with the latest data
      setFeeCalculator(structuredClone(getFormValues()));
    };

    const onSelectPortfolio = async (
      portfolioSetup: AnalysisFeePortfolioSetupDTO,
      itemIndex: number,
    ) => {
      if (!portfolioSetup.selectedPortfolioId) {
        const newPortfolioSetups = portfolioSetups?.map((item, index) => {
          if (index !== itemIndex) return item;
          return {
            ...item,
            selectedPortfolioId: null,
            hasAllocationError: false,
            isMissingInvestment: false,
            missingInvestmentTooltip: null,
            isMissingInvestmentOption: false,
            missingInvestmentOptionTooltip: null,
            isBelowMinimumInvestmentSize: false,
            belowMinimumInvestmentSizeTooltip: null,
            hasUnavailableInvestmentOption: false,
            unavailableInvestmentOptionTooltip: null,
            hasUnavailableInvestment: false,
            unavailableInvestmentTooltip: null,
            hasCashNotAvailableInSuperError: false,
            hasCashNotAvailableInIDPSError: false,
          };
        });

        updatePortfolioSetups(newPortfolioSetups);
        return;
      }

      const portfolioAssignmentItems = [
        getPortfolioAssignmentItem(portfolioSetup),
      ];

      const portfolioValidation = await checkAnalysisPortfolioAssignment(
        analysisId || "",
        {
          portfolioId: portfolioSetup.selectedPortfolioId ?? "",
          totalBalance: calculatedTotals.total,
          items: portfolioAssignmentItems,
          hasIDPSBalance: hasIDPS,
          hasSuperBalance: hasSuper,
        },
        investmentTypeSelections,
      );

      const updatedPortfolioSetups = portfolioSetups?.map((item, index) => {
        if (index !== itemIndex) return item;
        return portfolioSetup;
      });

      const compiledValidateResult = compileOverwritePortfolioValidationResult({
        portfolioSetups: updatedPortfolioSetups ?? [],
        portfolioValidation,
        portfolioAssignmentItems: portfolioAssignmentItems,
        portfolios: portfolios ?? [],
        feeCalculators: feeCalculators,
        investmentTypeSelection: investmentTypeSelections,
        hasIDPSBalance: hasIDPS,
        hasSuperBalance: hasSuper,
      });

      updatePortfolioSetups(compiledValidateResult);
    };

    const handleOnOverwritePortfolio = async (
      updatedPortfolio?: AnalysisFeePortfolioDTO,
    ) => {
      const updatedPortfolios = portfolios?.map((portfolio) => {
        return portfolio.id === updatedPortfolio?.id
          ? updatedPortfolio
          : portfolio;
      });

      updatePortfolios(updatedPortfolios);
      setFeeCalculator(structuredClone(getFormValues()));

      const portfolioAssignmentItems: PortfolioAssignmentItemDTO[] = [];

      portfolioSetups?.forEach((portfolioSetup) => {
        if (portfolioSetup.selectedPortfolioId === updatedPortfolio?.id) {
          portfolioAssignmentItems.push(
            getPortfolioAssignmentItem(portfolioSetup),
          );
        }
      });

      const portfolioValidation = await checkAnalysisPortfolioAssignment(
        analysisId || "",
        {
          portfolioId: updatedPortfolio?.id ?? "",
          totalBalance: calculatedTotals.total,
          items: portfolioAssignmentItems,
          hasIDPSBalance: hasIDPS,
          hasSuperBalance: hasSuper,
          shouldSaveWarning: true,
        },
        investmentTypeSelections,
      );

      const compiledValidateResult = compileOverwritePortfolioValidationResult({
        portfolioSetups: portfolioSetups ?? [],
        portfolioValidation,
        portfolioAssignmentItems,
        portfolios: updatedPortfolios ?? [],
        feeCalculators,
        investmentTypeSelection: investmentTypeSelections,
        hasIDPSBalance: hasIDPS,
        hasSuperBalance: hasSuper,
      });

      updatePortfolioSetups(compiledValidateResult);
    };

    const handleOnModalClose = () => {
      toggleModalOpen(false);
      setModalType(undefined);
      setSelectedPortfolioSetup(undefined);
      setSelectedPortfolioSetupIndex(undefined);
      setPortfolio(undefined);
    };
    // #endregion Handlers

    const debounceUpdateValueByFormData = useMemo(
      () =>
        debounce(async () => {
          const portfolioAssignmentItems: PortfolioAssignmentItemDTO[] = [];

          let isEmptySelectedPortfolioId = true;
          portfolioSetups?.forEach((portfolioSetup) => {
            if (portfolioSetup.selectedPortfolioId)
              isEmptySelectedPortfolioId = false;
            portfolioAssignmentItems.push(
              getPortfolioAssignmentItem(portfolioSetup),
            );
          });

          if (isEmptySelectedPortfolioId) {
            setInitialValidatedPortfolioSetup(true);
            return;
          }

          const portfolioValidation = await checkAnalysisPortfolioAssignment(
            analysisId ?? "",
            {
              portfolioId: "",
              totalBalance: calculatedTotals.total,
              items: portfolioAssignmentItems,
              hasIDPSBalance: hasIDPS,
              hasSuperBalance: hasSuper,
              shouldSaveWarning: true,
            },
            investmentTypeSelections,
          );

          const compiledValidateResult =
            compileOverwritePortfolioValidationResult({
              portfolioSetups: portfolioSetups ?? [],
              portfolioValidation,
              portfolioAssignmentItems,
              portfolios: portfolios ?? [],
              feeCalculators,
              investmentTypeSelection: investmentTypeSelections,
              hasIDPSBalance: hasIDPS,
              hasSuperBalance: hasSuper,
            });

          const originalPortfolioSetups = structuredClone(portfolioSetups);
          const isContainInvalidResult = compiledValidateResult.some(
            (setup) => {
              const {
                hasAllocationError,
                hasUnavailableInvestmentOption,
                hasUnavailableInvestment,
                isMissingInvestment,
                isMissingInvestmentOption,
              } = setup;
              return (
                hasAllocationError ||
                hasUnavailableInvestmentOption ||
                hasUnavailableInvestment ||
                isMissingInvestment ||
                isMissingInvestmentOption
              );
            },
          );

          if (!isEqual(originalPortfolioSetups, compiledValidateResult)) {
            updatePortfolioSetups(
              compiledValidateResult,
              isContainInvalidResult,
            );
          }
          setInitialValidatedPortfolioSetup(true);
        }, 500),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        portfolioSetups,
        analysisId,
        calculatedTotals.total,
        hasIDPS,
        hasSuper,
        investmentTypeSelections,
      ],
    );

    useEffect(() => {
      debounceUpdateValueByFormData();
      return () => debounceUpdateValueByFormData.cancel();
      // eslint-disable-next-line
    }, [calculatedTotals.total, investmentTypeSelections]);

    useUnmount(() => {
      setInitialValidatedPortfolioSetup(false);
    });

    return (
      <SHStack
        sx={{
          width: { xs: "100%", md: 520 },
          "& .MuiAutocomplete-endAdornment": {
            paddingRight: "0px",
          },
          "& .MuiAutocomplete-endAdornment .MuiAutocomplete-clearIndicator": {
            marginRight: "0px",
            visibility: "visible",
            svg: {
              path: {
                fill: palette.common.black,
              },
            },
          },

          "& .MuiAutocomplete-endAdornment .MuiAutocomplete-clearIndicator:hover":
            {
              svg: {
                path: {
                  fill: palette.error[200],
                },
              },
            },
        }}
      >
        {portfolioSetups?.map((setup, setupIndex) => {
          const isLastItem = setupIndex === portfolioSetups.length - 1;

          return (
            <SetupItem
              key={setupIndex}
              isReview={!isSupplierGroup}
              data={setup}
              portfolios={portfolioSelections}
              itemIndex={setupIndex}
              isLastItem={isLastItem}
              isSectionDisabled={isValidatePortfolioSetup || disabled}
              onEditButtonClick={onEditPortfolio}
              onCreateButtonClick={onCreatePortfolio}
              onSelectPortfolio={async (
                portfolioSetup: AnalysisFeePortfolioSetupDTO,
                itemIndex: number,
              ) => {
                toggleIsValidatePortfolioSetup(true);
                onSelectPortfolio(portfolioSetup, itemIndex).then(() => {
                  setTimeout(() => toggleIsValidatePortfolioSetup(false), 300);
                });
              }}
            />
          );
        })}

        <AnalysisFeePortfolioSetupModal
          key={`portfolio-setup-modal-${modalOpen}`}
          analysisId={analysisId || ""}
          type={modalType}
          open={modalOpen}
          portfolioSetup={selectedPortfolioSetup}
          portfolioSetupIndex={selectedPortfolioSetupIndex}
          portfolio={portfolio}
          calculatedTotal={calculatedTotals}
          feeCalculators={feeCalculators}
          showFeeCalculatorSection={investmentTypeSelections}
          onNewPortfolioCreated={handleOnNewPortfolioCreated}
          onClose={handleOnModalClose}
          onOverwritePortfolio={handleOnOverwritePortfolio}
        />

        <SHDialog
          maxWidth="sm"
          width={450}
          open={modalNoBalance}
          body={
            "At least one entity/account must have a balance to add a portfolio"
          }
          footer={
            <SHStack
              justifyContent="center"
              alignItems="center"
              direction={"row"}
              spacing={7}
            >
              <SHButton
                color="primary"
                variant="contained"
                size="extraMedium"
                onClick={() => {
                  toggleNoBalanceModal(false);
                }}
              >
                Ok
              </SHButton>
            </SHStack>
          }
        />
      </SHStack>
    );
  },
);
